import React, { useState } from 'react';
import { Box, CircularProgress, Stack, TextField, alpha } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import IconHandler from 'components/common/icon/IconHandler';
import IconMi from 'components/common/icon/IconMi';
import { conciergeAPI } from 'api/concierge';
import useBundleTranslation from 'i18n';

interface IProps {
    questionId: string;
    question: string;
    options: string[];
    botIcon?: string;
    questionIndex: number;
    totalQuestions: number;
    onAnswered: (answer: string | null) => void;
}

export default function ClarificationQuestion({
    questionId,
    question,
    options,
    botIcon,
    questionIndex,
    totalQuestions,
    onAnswered,
}: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);

    const conciergeSettings = useSelector((state: any) => state.auth?.configuration?.conciergeSettings) ?? {};
    const iconProps = {
        type: botIcon !== undefined ? 'upload' : conciergeSettings.bot_icon_ind,
        value:
            botIcon !== undefined
                ? botIcon
                : conciergeSettings.bot_icon_ind === 'upload'
                  ? `data:image/png;base64,${conciergeSettings.bot_icon_data}`
                  : conciergeSettings.bot_icon_name,
    };
    const [customAnswer, setCustomAnswer] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async (answer: string | null) => {
        if (loading) return;
        setLoading(true);
        try {
            await conciergeAPI.answerClarificationQuestion(questionId, answer);
        } catch {
            // ignore — stream continues regardless
        } finally {
            setLoading(false);
            onAnswered(answer);
        }
    };

    const handleCustomSubmit = () => {
        const trimmed = customAnswer.trim();
        if (trimmed) {
            submit(trimmed);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleCustomSubmit();
        }
    };

    return (
        <Stack
            direction="row"
            sx={{
                borderTop: (theme: Theme) => `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                background: (theme: Theme) =>
                    `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0)} 40%)`,
                pt: 2,
                pr: 2,
                pb: 1.5,
                pl: 2,
                gap: '12px',
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    width: '24px',
                    height: '24px',
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'inline-flex',
                    color: 'primary.main',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        display: 'inline-flex',
                        borderRadius: '50%',
                        overflow: 'hidden',
                    }}
                >
                    <IconHandler icon={iconProps} sx={{ fontSize: '24px' }} />
                </Box>
            </Box>

            <Stack sx={{ minWidth: 0, width: '100%', gap: '12px' }}>
                <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
                        <Box sx={{ fontSize: '13px', color: 'primary.main', fontWeight: 500 }}>
                            {t('clarification.to_clarify')}
                        </Box>
                        <Box sx={{ fontSize: '12px', color: 'text.secondary' }}>
                            {t('clarification.pager', { index: questionIndex, total: totalQuestions })}
                        </Box>
                    </Stack>
                    <Box sx={{ fontSize: '13px', color: 'text.primary' }}>{question}</Box>
                </Box>

                <Stack sx={{ gap: '4px' }}>
                    {options.map((option, idx) => (
                        <Stack
                            key={idx}
                            direction="row"
                            alignItems="center"
                            spacing={0}
                            onClick={() => !loading && submit(option)}
                            sx={{
                                pr: 0,
                                height: '28px',
                                borderRadius: '4px',
                                border: (theme: Theme) => `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                                cursor: loading ? 'default' : 'pointer',
                                backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.04),
                                '&:hover': !loading
                                    ? {
                                          borderColor: 'primary.main',
                                          backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
                                      }
                                    : undefined,
                            }}
                        >
                            <Box
                                sx={{
                                    width: '26px',
                                    pr: '1px',
                                    flexShrink: 0,
                                    alignSelf: 'stretch',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
                                    borderRadius: '3px 0px 0px 3px',
                                    fontSize: '12px',
                                    color: 'text.secondary',
                                }}
                            >
                                {idx + 1}
                            </Box>
                            <Box sx={{ fontSize: '13px', color: 'primary.main', pl: '8px' }}>{option}</Box>
                        </Stack>
                    ))}

                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            value={customAnswer}
                            onChange={(e) => setCustomAnswer(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Something else"
                            disabled={loading}
                            size="small"
                            fullWidth
                            sx={{ '.MuiInputBase-input': { fontSize: '13px', py: '6px' } }}
                        />
                        {customAnswer.trim() ? (
                            <Box
                                onClick={() => !loading && handleCustomSubmit()}
                                sx={{
                                    flexShrink: 0,
                                    width: '28px',
                                    height: '28px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: 'primary.main',
                                    cursor: loading ? 'default' : 'pointer',
                                    opacity: loading ? 0.5 : 1,
                                    '&:hover': !loading ? { opacity: 0.8 } : undefined,
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={14} />
                                ) : (
                                    <IconMi icon="send" sx={{ fontSize: '16px' }} />
                                )}
                            </Box>
                        ) : (
                            <Box
                                onClick={() => !loading && submit(null)}
                                sx={{
                                    flexShrink: 0,
                                    width: '49px',
                                    height: '28px',
                                    px: '12px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontSize: '13px',
                                    color: 'text.secondary',
                                    cursor: loading ? 'default' : 'pointer',
                                    userSelect: 'none',
                                    backgroundColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.04),
                                    border: (theme: Theme) => `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
                                    borderRadius: '3px',
                                    opacity: loading ? 0.5 : 1,
                                    '&:hover': !loading
                                        ? {
                                              backgroundColor: (theme: Theme) =>
                                                  alpha(theme.palette.text.primary, 0.08),
                                              borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.32),
                                          }
                                        : undefined,
                                }}
                            >
                                {loading ? <CircularProgress size={14} /> : 'Skip'}
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
}
